import { useState, useCallback } from 'react';
import { patientAPI } from '../../../api/patient';
import { appointmentAPI } from '../../../api/appointment';

// Calculate age from date of birth
const calculateAge = (dob) => {
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age.toString();
};

export const usePatientData = () => {
  const [patientData, setPatientData] = useState({
    // Basic Information
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
    sex: '',
    dob: '',
    bloodGroup: '',

    // Address Information
    personalAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },

    // Office Details
    officeDetail: {
      company: '',
      designation: '',
      officeAddress: ''
    },

    // Vitals
    vitals: {
      heightCm: '',
      weightKg: '',
      bmi: '',
      bpSys: '',
      bpDia: '',
      pulse: '',
      temperature: '',
      oxygenSaturation: '',
      respiration: ''
    },

    // Medical History Arrays
    drugAllergies: [],
    allergies: [],
    currentMedications: [],

    // Medical Form Fields
    chiefComplaint: '',
    historyOfPresentIllness: '',
    pastHistory: '',
    pastMedicalHistory: '',
    medicalHistory: '',
    physicalExamination: '',

    // Family and Case Information
    familyId: '',
    relationshipToHead: 'Self',
    caseDone: false,

    // Additional fields for UI
    calculatedAge: '',
    fullName: ''
  });

  const [loading, setLoading] = useState(false);
  const [currentAppointmentId, setCurrentAppointmentId] = useState(null);

  // Helper to normalize simple text (capitalize first letter)
  const capitalize = (val) => (typeof val === 'string' && val.length ? val.charAt(0).toUpperCase() + val.slice(1) : val);

  // Map appointment.patient (object or id) to our patientData shape for instant UI prefill
  const prefillFromAppointment = (apt) => {
    const p = apt?.patient && typeof apt.patient === 'object' ? apt.patient : null;
    if (!p) return null;
    const sex = p.sex || p.gender || '';
    const dob = p.dob || p.dateOfBirth || '';
    const calculatedAge = dob ? calculateAge(dob) : '';
    return {
      firstName: p.firstName || '',
      lastName: p.lastName || '',
      mobile: p.mobile || p.phone || '',
      email: p.email || '',
      sex: sex || '',
      dob,
      bloodGroup: p.bloodGroup || '',
      photo: p.photo || null,
      personalAddress: {
        street: p.personalAddress?.street || '',
        city: p.personalAddress?.city || p.personalAddress?.village || '',
        state: p.personalAddress?.state || '',
        zipCode: p.personalAddress?.zipCode || '',
        country: p.personalAddress?.country || ''
      },
      officeDetail: {
        company: p.officeDetail?.company || p.officeDetail?.officeName || '',
        designation: p.officeDetail?.designation || '',
        officeAddress: p.officeDetail?.officeAddress || p.officeDetail?.address || ''
      },
      vitals: {
        heightCm: p.vitals?.heightCm || '',
        weightKg: p.vitals?.weightKg || '',
        bmi: p.vitals?.bmi || '',
        bpSys: p.vitals?.bpSys || p.vitals?.bpSystolic || '',
        bpDia: p.vitals?.bpDia || p.vitals?.bpDiastolic || '',
        pulse: p.vitals?.pulse || '',
        temperature: p.vitals?.temperature || '',
        oxygenSaturation: p.vitals?.oxygenSaturation || '',
        respiration: p.vitals?.respiration || ''
      },
      // Medical form fields from appointment
      chiefComplaint: apt?.complaint || '',
      physicalExamination: apt?.physicalExaminationFindings || '',
      calculatedAge,
      fullName: `${p.firstName || ''} ${p.lastName || ''}`.trim()
    };
  };

  // Load patient details when selected
  const loadPatientData = async (appointment) => {
    try {
      setLoading(true);
      // 1) Instant prefill from the appointment payload (ensures UI shows data right away)
      const prefill = prefillFromAppointment(appointment);
      if (prefill) setPatientData(prev => ({ ...prev, ...prefill }));

      // Fetch appointment details to get the latest complaint
      let appointmentDetails = null;
      if (appointment._id) {
        try {
          const appointmentResponse = await appointmentAPI.getById(appointment._id);
          if (appointmentResponse.success) {
            appointmentDetails = appointmentResponse.data;
            setCurrentAppointmentId(appointment._id);
          }
        } catch (err) {
          console.error('Error fetching appointment details:', err);
          // Fallback to using the appointment object passed in
          appointmentDetails = appointment;
          setCurrentAppointmentId(appointment._id);
        }
      } else {
        appointmentDetails = appointment;
        setCurrentAppointmentId(null);
      }

      // Fetch patient details
      const patientId = appointment.patient?._id || appointment.patient;
      const patientResponse = await patientAPI.getById(patientId);
      console.log('Patient data:', patientResponse.data.patient);
      console.log('Appointment details:', appointmentDetails);

      if (patientResponse.success && patientResponse.data.patient) {
        const patient = patientResponse.data.patient;
        const calculatedAge = (patient.dob || patient.dateOfBirth) ? calculateAge(patient.dob || patient.dateOfBirth) : '';

        setPatientData({
          // Basic Information
          firstName: patient.firstName || prefill?.firstName || '',
          lastName: patient.lastName || prefill?.lastName || '',
          mobile: patient.mobile || patient.phone || prefill?.mobile || '',
          email: patient.email || prefill?.email || '',
          sex: patient.sex || patient.gender || prefill?.sex || '',
          dob: patient.dob || patient.dateOfBirth || prefill?.dob || '',
          bloodGroup: patient.bloodGroup || prefill?.bloodGroup || '',
          photo: patient.photo || prefill?.photo || null,

          // Address Information
          personalAddress: {
            street: patient.personalAddress?.street || prefill?.personalAddress?.street || '',
            city: patient.personalAddress?.city || patient.personalAddress?.village || prefill?.personalAddress?.city || '',
            state: patient.personalAddress?.state || prefill?.personalAddress?.state || '',
            zipCode: patient.personalAddress?.zipCode || prefill?.personalAddress?.zipCode || '',
            country: patient.personalAddress?.country || prefill?.personalAddress?.country || ''
          },

          // Office Details
          officeDetail: {
            company: patient.officeDetail?.company || patient.officeDetail?.officeName || prefill?.officeDetail?.company || '',
            designation: patient.officeDetail?.designation || prefill?.officeDetail?.designation || '',
            officeAddress: patient.officeDetail?.officeAddress || patient.officeDetail?.address || prefill?.officeDetail?.officeAddress || ''
          },

          // Vitals
          vitals: {
            heightCm: patient.vitals?.heightCm || prefill?.vitals?.heightCm || '',
            weightKg: patient.vitals?.weightKg || prefill?.vitals?.weightKg || '',
            bmi: patient.vitals?.bmi || prefill?.vitals?.bmi || '',
            bpSys: patient.vitals?.bpSys || patient.vitals?.bpSystolic || prefill?.vitals?.bpSys || '',
            bpDia: patient.vitals?.bpDia || patient.vitals?.bpDiastolic || prefill?.vitals?.bpDia || '',
            pulse: patient.vitals?.pulse || prefill?.vitals?.pulse || '',
            temperature: patient.vitals?.temperature || prefill?.vitals?.temperature || '',
            oxygenSaturation: patient.vitals?.oxygenSaturation || prefill?.vitals?.oxygenSaturation || '',
            respiration: patient.vitals?.respiration || prefill?.vitals?.respiration || ''
          },

          // Medical History Arrays
          drugAllergies: Array.isArray(patient.drugAllergies) ? patient.drugAllergies : [],
          allergies: Array.isArray(patient.allergies) ? patient.allergies : [],
          currentMedications: Array.isArray(patient.currentMedications) ? patient.currentMedications : [],

          // Medical Form Fields - Get complaint and physical examination from appointment details
          chiefComplaint: appointmentDetails?.complaint || prefill?.chiefComplaint || '',
          historyOfPresentIllness: patient.historyOfPresentIllness || '',
          pastHistory: patient.pastHistory || patient.pastMedicalHistory || '',
          pastMedicalHistory: patient.pastMedicalHistory || patient.pastHistory || '',
          medicalHistory: patient.medicalHistory || '',
          physicalExamination: appointmentDetails?.physicalExaminationFindings || patient.physicalExamination || '',

          // Family and Case Information
          familyId: patient.familyId || prefill?.familyId || '',
          relationshipToHead: patient.relationshipToHead || prefill?.relationshipToHead || 'Self',
          caseDone: patient.caseDone || appointmentDetails?.status === 'Completed',

          // Additional fields for UI
          calculatedAge,
          fullName: `${patient.firstName || prefill?.firstName || ''} ${patient.lastName || prefill?.lastName || ''}`.trim()
        });
      }
    } catch (error) {
      console.error('Error loading patient details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update appointment complaint
  // Update appointment with complaint and physical examination findings
  const updateAppointmentDetails = async (complaint, physicalExamination) => {
    if (!currentAppointmentId) {
      console.warn('No current appointment ID available');
      return false;
    }

    try {
      const updateData = {};

      // Add complaint if provided
      if (complaint !== undefined && complaint !== null) {
        updateData.complaint = complaint;
      }

      // Add physical examination findings if provided
      if (physicalExamination !== undefined && physicalExamination !== null) {
        updateData.physicalExaminationFindings = physicalExamination;
      }

      // Only make API call if there's data to update
      if (Object.keys(updateData).length === 0) {
        console.warn('No data to update in appointment');
        return false;
      }

      const response = await appointmentAPI.update(currentAppointmentId, updateData);

      if (response.success) {
        console.log('Appointment details updated successfully:', updateData);
        return true;
      } else {
        console.error('Failed to update appointment details:', response.message);
        return false;
      }
    } catch (error) {
      console.error('Error updating appointment details:', error);
      return false;
    }
  };

  // Legacy function for backward compatibility
  const updateAppointmentComplaint = async (newComplaint) => {
    return await updateAppointmentDetails(newComplaint, undefined);
  };

  // Reset patient data
  const resetPatientData = useCallback(() => {
    setPatientData({
      firstName: '', lastName: '', mobile: '', email: '', sex: 'Male', dob: '', bloodGroup: '',
      personalAddress: { street: '', city: '', state: '', zipCode: '', country: '' },
      officeDetail: { company: '', designation: '', officeAddress: '' },
      vitals: { heightCm: '', weightKg: '', bmi: '', bpSys: '', bpDia: '', pulse: '', temperature: '', oxygenSaturation: '', respiration: '' },
      drugAllergies: [], allergies: [], currentMedications: [],
      chiefComplaint: '', historyOfPresentIllness: '', pastHistory: '', pastMedicalHistory: '', medicalHistory: '', physicalExamination: '',
      familyId: '', relationshipToHead: 'Self', caseDone: false, calculatedAge: '', fullName: ''
    });
    setCurrentAppointmentId(null);
  }, []);

  // Save patient information
  const handleSavePatient = async (selectedPatient) => {
    if (!selectedPatient) {
      return { success: false, message: 'Please select a patient first' };
    }

    try {
      setLoading(true);

      // Prepare patient data for API
      const updateData = {
        firstName: patientData.firstName,
        lastName: patientData.lastName,
        mobile: patientData.mobile,
        email: patientData.email,
        sex: patientData.sex,
        dob: patientData.dob,
        bloodGroup: patientData.bloodGroup,
        personalAddress: patientData.personalAddress,
        officeDetail: patientData.officeDetail,
        vitals: patientData.vitals,
        // Medical history arrays
        drugAllergies: Array.isArray(patientData.drugAllergies) ? patientData.drugAllergies : [],
        allergies: Array.isArray(patientData.allergies) ? patientData.allergies : [],
        currentMedications: Array.isArray(patientData.currentMedications) ? patientData.currentMedications : [],
        // Medical form fields
        chiefComplaint: patientData.chiefComplaint,
        historyOfPresentIllness: patientData.historyOfPresentIllness,
        pastHistory: patientData.pastHistory || patientData.pastMedicalHistory,
        // Defensive: Ensure medicalHistory is a string, not an object
        medicalHistory: typeof patientData.medicalHistory === 'string' ? patientData.medicalHistory : '',
        physicalExamination: patientData.physicalExamination,
        familyId: patientData.familyId,
        relationshipToHead: patientData.relationshipToHead,
        caseDone: patientData.caseDone
      };

      // Remove empty fields
      Object.keys(updateData).forEach(key => {
        if (typeof updateData[key] === 'object' && updateData[key] !== null) {
          Object.keys(updateData[key]).forEach(subKey => {
            if (!updateData[key][subKey]) {
              delete updateData[key][subKey];
            }
          });
          if (Object.keys(updateData[key]).length === 0) {
            delete updateData[key];
          }
        } else if (!updateData[key]) {
          delete updateData[key];
        }
      });

      const response = await patientAPI.update(selectedPatient.patient._id || selectedPatient.patient, updateData);

      if (response.success) {
        // Also update appointment with complaint and physical examination findings if they have changed
        if (currentAppointmentId) {
          await updateAppointmentDetails(
            patientData.chiefComplaint || undefined,
            patientData.physicalExamination || undefined
          );
        }
        return { success: true, message: 'Patient information updated successfully!' };
      } else {
        return { success: false, message: 'Failed to update patient information' };
      }
    } catch (error) {
      console.error('Error saving patient:', error);
      return { success: false, message: error.message || 'Unknown error' };
    } finally {
      setLoading(false);
    }
  };

  // Create new patient
  const handleCreatePatient = async (userId) => {
    try {
      setLoading(true);

      // Validate required fields
      if (!patientData.firstName || !patientData.lastName || !patientData.mobile || !patientData.sex) {
        return { success: false, message: 'Please fill in all required fields (First Name, Last Name, Mobile, Gender)' };
      }

      // Prepare patient data for API
      const createData = {
        firstName: patientData.firstName,
        lastName: patientData.lastName,
        mobile: patientData.mobile,
        email: patientData.email,
        sex: patientData.sex,
        dob: patientData.dob || new Date().toISOString().split('T')[0], // Default to today if not provided
        bloodGroup: patientData.bloodGroup,
        personalAddress: patientData.personalAddress,
        officeDetail: patientData.officeDetail,
        vitals: patientData.vitals,
        // Medical history arrays
        drugAllergies: Array.isArray(patientData.drugAllergies) ? patientData.drugAllergies : [],
        allergies: Array.isArray(patientData.allergies) ? patientData.allergies : [],
        currentMedications: Array.isArray(patientData.currentMedications) ? patientData.currentMedications : [],
        // Medical form fields
        chiefComplaint: patientData.chiefComplaint,
        historyOfPresentIllness: patientData.historyOfPresentIllness,
        pastHistory: patientData.pastHistory || patientData.pastMedicalHistory,
        // Defensive: Ensure medicalHistory is a string, not an object
        medicalHistory: typeof patientData.medicalHistory === 'string' ? patientData.medicalHistory : '',
        physicalExamination: patientData.physicalExamination,
        familyId: patientData.familyId,
        relationshipToHead: patientData.relationshipToHead,
        assignedDoctor: userId // Automatically assign to current doctor
      };

      // Remove empty nested objects
      Object.keys(createData).forEach(key => {
        if (typeof createData[key] === 'object' && createData[key] !== null) {
          Object.keys(createData[key]).forEach(subKey => {
            if (!createData[key][subKey]) {
              delete createData[key][subKey];
            }
          });
          if (Object.keys(createData[key]).length === 0) {
            delete createData[key];
          }
        }
      });

      const response = await patientAPI.create(createData);

      if (response.success) {
        resetPatientData();
        return { success: true, message: 'New patient created successfully!' };
      } else {
        return { success: false, message: 'Failed to create patient' };
      }
    } catch (error) {
      console.error('Error creating patient:', error);
      return { success: false, message: error.message || 'Unknown error' };
    } finally {
      setLoading(false);
    }
  };

  return {
    patientData,
    setPatientData,
    loading,
    loadPatientData,
    resetPatientData,
    handleSavePatient,
    handleCreatePatient,
    updateAppointmentComplaint, // Legacy - kept for backward compatibility
    updateAppointmentDetails, // New function for updating both complaint and physical examination
    currentAppointmentId,
    calculateAge
  };
};
